/**
 * API Client for Sootak Mini App
 * Handles all HTTP requests to the backend API with authentication
 */

// Type definitions for API responses
export interface AuthResponse {
  token: string;
  user_id: string;
  bale_user_id: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

export interface BalanceResponse {
  balance: {
    total_credits: number;
    available_credits: number;
    reserved_credits: number;
    equivalent_minutes: number;
  };
  current_plan: {
    id: string;
    name: string;
    name_fa: string;
    tier: string;
    expires_at?: string | null;
    features: PlanFeatures;
  } | null;
  usage_summary?: {
    current_period_credits_used: number;
    current_period_minutes: number;
    period_start: string;
  } | null;
}

export interface UsageEvent {
  id: string;
  type: string;
  credits_deducted: number;
  equivalent_minutes?: number | null;
  created_at: string;
  transcription_id?: string | null;
  duration_sec?: number | null;
  feature_type?: string | null;
}

export interface UsageResponse {
  usage: UsageEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface PlanFeatures {
  ai_features_enabled: boolean;
  requests_per_hour: number;
  concurrent_jobs: number;
  priority_processing: boolean;
}

export interface Plan {
  id: string;
  name: string;
  name_fa: string;
  tier: string;
  price_toman: number;
  credits: number;
  price_per_credit_toman: number;
  validity_period_days?: number | null;
  features: PlanFeatures;
}

export interface PlansResponse {
  plans: Plan[];
  top_up_options: Array<{
    credits: number;
    price_toman: number;
    price_per_credit_toman: number;
  }>;
}

export interface Transaction {
  id: string;
  type: string;
  amount_toman: number;
  credits_added: number;
  payment_method: string;
  status: string;
  created_at: string;
  completed_at?: string | null;
  plan?: {
    id: string;
    name: string;
    name_fa: string;
  } | null;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface PurchaseInitiateRequest {
  type?: 'subscription' | 'top_up';
  plan_id: string;
  credits?: number;
  payment_method: 'zarinpal' | 'bale_wallet';
}

export interface PurchaseInitiateResponse {
  transaction_id: string;
  payment_url?: string;
  authority?: string;
  invoice_message_id?: number;
}

/**
 * API Client class for making authenticated requests to the backend
 */
class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Set authentication token and store in localStorage
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  /**
   * Get authentication token from memory or localStorage
   */
  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('auth_token');
    }
    return this.token;
  }

  /**
   * Clear authentication token from memory and localStorage
   */
  clearToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  /**
   * Exchange Bale InitData for JWT token
   */
  async exchangeInitData(initData: string): Promise<AuthResponse> {
    const response = await fetch(`${this.baseUrl}/v1/miniapp/auth/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ init_data: initData }),
    });

    if (!response.ok) {
      throw new Error(await this.getErrorMessage(response, 'خطا در احراز هویت'));
    }

    return response.json();
  }

  /**
   * Get user's credit balance
   */
  async getBalance(): Promise<BalanceResponse> {
    return this.authenticatedRequest('/v1/miniapp/balance');
  }

  /**
   * Get usage history with pagination
   */
  async getUsage(page: number = 1, pageSize: number = 20): Promise<UsageResponse> {
    const offset = (page - 1) * pageSize;
    return this.authenticatedRequest(`/v1/miniapp/usage?limit=${pageSize}&offset=${offset}`);
  }

  /**
   * Get available subscription plans and top-up options
   */
  async getPlans(): Promise<PlansResponse> {
    return this.authenticatedRequest('/v1/miniapp/plans');
  }

  /**
   * Get transaction history with pagination
   */
  async getTransactions(page: number = 1, pageSize: number = 20, status?: string): Promise<TransactionsResponse> {
    const offset = (page - 1) * pageSize;
    let url = `/v1/miniapp/transactions?limit=${pageSize}&offset=${offset}`;
    if (status) {
      url += `&status=${status}`;
    }
    return this.authenticatedRequest(url);
  }

  /**
   * Initiate a purchase (plan or top-up)
   */
  async initiatePurchase(request: PurchaseInitiateRequest): Promise<PurchaseInitiateResponse> {
    return this.authenticatedRequest('/v1/miniapp/purchase/initiate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ type: 'subscription', ...request }),
    });
  }

  /**
   * Validate authentication token
   */
  async validateToken(): Promise<{ valid: boolean; user_id?: string; provider?: string }> {
    try {
      return await this.authenticatedRequest('/v1/miniapp/auth/validate', {
        method: 'POST',
      });
    } catch {
      return { valid: false };
    }
  }

  /**
   * Revoke authentication token (logout)
   */
  async revokeToken(): Promise<void> {
    try {
      await this.authenticatedRequest('/v1/miniapp/auth/revoke', {
        method: 'POST',
      });
    } finally {
      this.clearToken();
    }
  }

  /**
   * Make an authenticated request to the API
   * @private
   */
  private async authenticatedRequest<T = unknown>(path: string, options: RequestInit = {}): Promise<T> {
    const token = this.getToken();
    if (!token) {
      throw new Error('احراز هویت نشده‌اید. لطفاً دوباره وارد شوید.');
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
      },
    });

    // Handle 401 Unauthorized - session expired
    if (response.status === 401) {
      this.clearToken();
      throw new Error('نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
    }

    // Handle other error responses
    if (!response.ok) {
      throw new Error(await this.getErrorMessage(response, 'خطا در ارتباط با سرور'));
    }

    return response.json();
  }

  private async getErrorMessage(response: Response, fallback: string): Promise<string> {
    const error = await response.json().catch(() => null);
    if (typeof error?.detail === 'string') {
      return error.detail;
    }
    if (typeof error?.detail?.message_fa === 'string') {
      return error.detail.message_fa;
    }
    if (typeof error?.detail?.message === 'string') {
      return error.detail.message;
    }
    if (typeof error?.error?.message === 'string') {
      return error.error.message;
    }
    return fallback;
  }
}

// Create and export a singleton instance
export const apiClient = new ApiClient(
  import.meta.env.VITE_API_BASE_URL || window.location.origin
);
